import React, { createContext, useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { getAuthUserSelector } from '@sophocles/features/auth/authSelectors';
import {
  determineIsAccounting,
  determineIsCrewManager,
  determineIsReadOnlySiteGroupUser,
  determineIsSales,
  determineIsSalesAdmin,
  determineIsShipping,
  determineIsTechnician,
  determineIsTechSupport,
  determineIsAtLeastSiteGroupAdmin,
  determineIsInventoryAdmin,
  determineIsODLEngineering,
  determineIsQuickBooks,
  userPermCheck,
} from '@sophocles/utils/userPermissionUtils';

interface User {
  get: (key: string, defaultValue?: any) => any;
}

interface UserContextProps {
  user: User;
  isSuperUser: boolean;
  isStaff: boolean;
  permissions: {
    isCrewManager: boolean;
    isTechnician: boolean;
    isAccounting: boolean;
    isShipping: boolean;
    isSales: boolean;
    isViewOnly: boolean;
    canViewUsers: boolean;
    canDeleteUsers: boolean;
  };
}

const UserContext = createContext<UserContextProps | undefined>(undefined);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const user = useSelector(getAuthUserSelector) as User;
  const groups = user.get('groups', []);
  const isSuperUser = user.get('is_superuser', false);
  const isStaff = user.get('is_staff', false);

  const permissions = useMemo(
    () => ({
      isCustomerAdmin: isSuperUser || groups.indexOf(7) > -1,
      isCrewManager: isSuperUser || determineIsCrewManager(groups),
      isTechnician: isSuperUser || determineIsTechnician(groups),
      isAccounting: isSuperUser || determineIsAccounting(groups),
      isTechSupport: isSuperUser || determineIsTechSupport(groups),
      isShipping: isSuperUser || determineIsShipping(groups),
      isSales: isSuperUser || determineIsSales(groups) || determineIsSalesAdmin(groups),
      isSalesAdmin: isSuperUser || determineIsSalesAdmin(groups),
      isAtLeastSiteGroupAdmin: isSuperUser || determineIsAtLeastSiteGroupAdmin(groups),
      isODLEngineering: isSuperUser || determineIsODLEngineering(groups),
      isInventoryAdmin: isSuperUser || determineIsInventoryAdmin(groups),
      isQuickBooksUser: isSuperUser || determineIsQuickBooks(groups),
      isReseller: isSuperUser || userPermCheck(user, 'pliny.view_other_customers'),
      canSeeInventory: userPermCheck(user, 'odl_inventory.view_radiolocation'),
      canEditSite: userPermCheck(user, 'pliny.change_site'),
      canEditSiteGroup: userPermCheck(user, 'pliny.change_sitegroup'),
      canCreateSiteGroup: userPermCheck(user, 'pliny.add_sitegroup'),
      canDeleteSiteGroup: userPermCheck(user, 'pliny.delete_sitegroup'),
      canEditSiteGroupCurfew: userPermCheck(user, 'pliny.change_sitegroupcurfew'),
      canEditCustomer: userPermCheck(user, 'pliny.change_customer'),
      canEditCustomerOptions: userPermCheck(user, 'pliny.change_customeroptions'),
      canEditBillingAgreements: userPermCheck(user, 'odl_billing.change_billingaccountagreement'),
      canEditCustomerAttachments: userPermCheck(user, 'pliny.change_customerattachment'),
      canAssignResellerCustomers: userPermCheck(user, 'pliny.add_resellercustomer'),
      canUnassignResellerCustomers: userPermCheck(user, 'pliny.delete_resellercustomer'),
      canChangeResellerDirection: userPermCheck(user, 'pliny.change_resellercustomer'),
      // Mirrors pliny's is_staff_or_superuser | view_inventory_groups: which
      // groups are ODL's own warehouses is an internal billing arrangement.
      canViewInventoryGroups: isSuperUser || isStaff || userPermCheck(user, 'pliny.view_inventory_groups'),
      canViewTickets: isStaff,
      canViewRMAs: userPermCheck(user, 'rma.view_returnmaterialauthorization'),
      isViewOnly: !isSuperUser && !isStaff && determineIsReadOnlySiteGroupUser(groups),
      canViewUsers: isSuperUser || isStaff || !determineIsReadOnlySiteGroupUser(groups),
      canUpgradeFirmware: isSuperUser || userPermCheck(user, 'pliny.can_upgrade_firmware'),
      // Same grant as pliny's CanDeleteUsers: staff, or the customer-admin group.
      canDeleteUsers: isStaff || isSuperUser || groups.indexOf(7) > -1,
    }),
    [user, groups, isSuperUser, isStaff],
  );

  return <UserContext.Provider value={{ user, isSuperUser, isStaff, permissions }}>{children}</UserContext.Provider>;
};

export const useUser = (): UserContextProps => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
